import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";
import { toast } from "sonner";

// Event-type options + their recommended recurrence defaults.
const EVENT_TYPES = [
  { id: "shiur",    label: "שיעור",       recDefault: "weekly" },
  { id: "yahrzeit", label: "יארצייט",     recDefault: "yearly_hebrew" },
  { id: "birthday", label: "יום הולדת",   recDefault: "yearly_hebrew" },
  { id: "reminder", label: "תזכורת",      recDefault: "none" },
];

const RECURRENCE_OPTIONS = [
  { id: "none",             label: "חד-פעמי" },
  { id: "yearly_hebrew",    label: "כל שנה (עברי)" },
  { id: "yearly_gregorian", label: "כל שנה (לועזי)" },
  { id: "weekly",           label: "כל שבוע" },
];

const COLORS = [
  { id: "gold",   label: "זהוב", cls: "bg-gold/80 border-gold" },
  { id: "navy",   label: "נייבי", cls: "bg-[#1E2A3A] border-[#1E2A3A]" },
  { id: "green",  label: "ירוק",  cls: "bg-emerald-600 border-emerald-700" },
  { id: "red",    label: "אדום",  cls: "bg-rose-600 border-rose-700" },
  { id: "purple", label: "סגול",  cls: "bg-violet-600 border-violet-700" },
];

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}


/**
 * EventForm — create or edit a personal calendar event.
 *
 * Props
 *  - open / onClose         : Dialog control
 *  - initialDate (ISO)      : seed the date field, e.g. when launched from a day cell
 *  - existing (event)       : edit mode — fields prefilled, save uses PUT
 *  - onSaved (event)        : called after a successful create/update
 */
export default function EventForm({ open, onClose, initialDate, existing, onSaved }) {
  const seedDate = existing?.date_gregorian || initialDate || todayIso();
  const [eventType, setEventType] = useState(existing?.event_type || "shiur");
  const [title, setTitle]         = useState(existing?.title || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [date, setDate]           = useState(seedDate);
  const [time, setTime]           = useState(existing?.time || "");
  const [recurrence, setRecurrence] = useState(existing?.recurrence || "none");
  const [color, setColor]         = useState(existing?.color || "gold");
  const [saving, setSaving]       = useState(false);

  // Reset state every time the dialog opens with new props.
  useEffect(() => {
    if (!open) return;
    setEventType(existing?.event_type || "shiur");
    setTitle(existing?.title || "");
    setDescription(existing?.description || "");
    setDate(existing?.date_gregorian || initialDate || todayIso());
    setTime(existing?.time || "");
    setRecurrence(existing?.recurrence || (
      EVENT_TYPES.find(t => t.id === (existing?.event_type || "shiur"))?.recDefault || "none"
    ));
    setColor(existing?.color || "gold");
  }, [open, existing, initialDate]);

  // When user changes type (and we're NOT editing) — re-suggest the
  // sensible recurrence default for that type.
  const onTypeChange = (t) => {
    setEventType(t);
    if (!existing) {
      const rec = EVENT_TYPES.find((x) => x.id === t)?.recDefault || "none";
      setRecurrence(rec);
    }
  };

  const adarNotice = useMemo(() => {
    // Surface the Adar convention hint when relevant: any yearly_hebrew
    // event anchored in (non-leap) Adar will potentially shift to
    // Adar II in leap years. We only know the user's Hebrew month
    // after the BE responds — but we can approximate by checking if
    // the anchor falls in Adar-typical Gregorian months (Feb / Mar).
    if (recurrence !== "yearly_hebrew") return false;
    try {
      const [yr, mo] = date.split("-").map(Number);
      // Feb 10 - Mar 25 is a safe envelope for any Adar day in any year.
      const d = new Date(yr, mo - 1, Number(date.split("-")[2]));
      const start = new Date(yr, 1, 10);
      const end   = new Date(yr, 2, 25);
      return d >= start && d <= end;
    } catch { return false; }
  }, [recurrence, date]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) {
      toast.error("יש להזין כותרת");
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      event_type: eventType,
      date_gregorian: date,
      time: time || null,
      recurrence,
      color,
    };
    try {
      const saved = existing
        ? await api.myEventUpdate(existing.id, payload)
        : await api.myEventCreate(payload);
      toast.success(existing ? "האירוע עודכן" : "האירוע נוסף ליומן");
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error("שגיאה בשמירה", {
        description: err?.response?.data?.detail || "נסה שוב",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        dir="rtl"
        data-testid="event-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-right">
            {existing ? "עריכת אירוע" : "הוספת אירוע חדש"}
          </DialogTitle>
          <DialogDescription className="text-right text-xs">
            יישמר ביומן האישי שלך וייסומן בלוח.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 mt-2" onSubmit={submit}>
          {/* Event type — pill row */}
          <div className="space-y-1.5">
            <Label className="text-xs">סוג האירוע</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="event-type-row">
              {EVENT_TYPES.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => onTypeChange(t.id)}
                  data-testid={`event-type-${t.id}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition ${
                    eventType === t.id
                      ? "bg-gold/15 text-foreground border-gold/50"
                      : "border-border/60 text-muted-foreground hover:border-gold/30"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-title" className="text-xs">כותרת</Label>
            <Input
              id="ev-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={120}
              dir="auto"
              data-testid="event-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-desc" className="text-xs">תיאור (אופציונלי)</Label>
            <Textarea
              id="ev-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={1000}
              dir="auto"
              data-testid="event-desc-input"
            />
          </div>

          {/* Date + time */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="ev-date" className="text-xs">תאריך</Label>
              <Input
                id="ev-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-time" className="text-xs">שעה (אופציונלי)</Label>
              <Input
                id="ev-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="event-time-input"
              />
            </div>
          </div>

          {/* Recurrence */}
          <div className="space-y-1.5">
            <Label className="text-xs">חזרה</Label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="recurrence-row">
              {RECURRENCE_OPTIONS.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setRecurrence(r.id)}
                  data-testid={`recurrence-${r.id}`}
                  className={`text-xs px-3 py-2 rounded-sm border text-right transition ${
                    recurrence === r.id
                      ? "bg-gold/15 text-foreground border-gold/50"
                      : "border-border/60 text-muted-foreground hover:border-gold/30"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {adarNotice && (
              <div
                className="mt-2 flex items-start gap-2 rounded-sm border border-gold/35 bg-gold/8 p-2 text-[11px] leading-snug text-foreground"
                data-testid="adar-notice"
              >
                <AlertCircle className="h-3.5 w-3.5 text-gold mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <span>
                  אם התאריך נופל באדר ושנה עברית הבאה היא מעוברת —
                  האירוע יציין באדר ב' (מנהג אשכנז).
                </span>
              </div>
            )}
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label className="text-xs">צבע</Label>
            <div className="flex gap-2" data-testid="color-row">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setColor(c.id)}
                  aria-label={c.label}
                  data-testid={`color-${c.id}`}
                  className={`h-7 w-7 rounded-full border-2 transition ${c.cls} ${
                    color === c.id
                      ? "ring-2 ring-gold ring-offset-1 ring-offset-background"
                      : "opacity-80 hover:opacity-100"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="event-cancel-btn"
            >
              ביטול
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="event-save-btn"
              className="bg-gold/90 hover:bg-gold text-[#1E2A3A]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (existing ? "עדכן" : "שמור אירוע")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
